import { stripHtmlTags } from '@poseidon/shared-utils/html';

import type { SendOutcomeNoticePureProps } from './SendOutcomeNoticePure';

/**
 * The host refused to send on purpose and the rep has to look at something first: a reply came in,
 * no channel is connected, the subject is missing. Thrown from `onSubmitStep`; the composer keeps
 * the text and shows the amber notice with the one action that unblocks it (when there is one).
 */
export class SendBlockedError extends Error {
  readonly title: string;
  readonly body?: string;
  readonly unblock?: { label: string; onClick: () => void };

  constructor(opts: { title: string; body?: string; unblock?: { label: string; onClick: () => void } }) {
    super(opts.title);
    this.name = 'SendBlockedError';
    this.title = opts.title;
    this.body = opts.body;
    this.unblock = opts.unblock;
  }
}

/**
 * The send itself did not happen (the provider refused it, the network dropped). Any thrown `Error`
 * reads as this; the class only exists to carry the provider's technical story for "More details".
 */
export class SendFailedError extends Error {
  readonly detail?: string;

  constructor(message: string, opts?: { detail?: string; cause?: unknown }) {
    super(message, opts?.cause != null ? { cause: opts.cause } : undefined);
    this.name = 'SendFailedError';
    this.detail = opts?.detail;
  }
}

const FALLBACK_FAILED_BODY = 'Something went wrong. Your message is still here, try again in a moment.';

/**
 * What the composer shows above Send after `onSubmitStep` rejected. The rep's text is untouched;
 * this only describes what happened and offers Copy (the Send button right below is the retry).
 */
export function sendOutcomeFromError(
  error: unknown,
  actions: { onCopy?: () => Promise<void> | void },
): Omit<SendOutcomeNoticePureProps, 'className'> {
  if (error instanceof SendBlockedError) {
    return { tone: 'blocked', title: error.title, body: error.body, unblock: error.unblock, onCopy: actions.onCopy };
  }
  const message = error instanceof Error && error.message.trim() !== '' ? error.message : FALLBACK_FAILED_BODY;
  const detail = error instanceof SendFailedError ? error.detail : undefined;
  return { tone: 'failed', title: 'Not sent.', body: message, detail, onCopy: actions.onCopy };
}

/** The composer's body is TipTap HTML; the clipboard gets the words. */
export function composerBodyToPlainText(html: string | null | undefined): string {
  if (html == null) return '';
  return stripHtmlTags(html.replace(/<\/(p|div|br|li)>/gi, '\n').replace(/<br\s*\/?>/gi, '\n'))
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}
